<!--
  A real <section> with a real heading, replacing the app-panel markup whose
  titles render at body weight 400 (web paper cut 14, refresh_plan.md
  3.1.6): the weakest text on the page it titles.

  `surface` defaults to "app" and is BYTE-FOR-BYTE the panel above: every
  existing caller (the Dispatch board included) is unaffected. "document" is
  MANIFEST's document treatment (refresh_plan.md section 8, "steal the
  document surfaces outright... a proof of delivery is a legal artifact and
  it should look like paper"), and it is a different surface class on
  purpose: a live board is data that changes under the dispatcher, a document
  is a record that has to look the same on screen, in print and faxed. Giving
  documents their own modifier rather than a colour prop keeps that
  distinction visible in the markup, not just in the token values it reads.
-->
<template>
  <section class="lw-c-panel" :class="{ 'lw-c-panel--document': surface === 'document' }">
    <header class="lw-c-panel__head">
      <div class="lw-c-panel__heading-group">
        <component :is="headingTag" class="lw-c-panel__title">
          <slot name="title">
            {{ title }}
          </slot>
        </component>
        <p v-if="subtitle" class="lw-c-panel__subtitle">
          {{ subtitle }}
        </p>
      </div>
      <div v-if="$slots.actions" class="lw-c-panel__actions">
        <slot name="actions" />
      </div>
    </header>
    <div class="lw-c-panel__body">
      <slot />
    </div>
  </section>
</template>

<script>
const MIN_HEADING_LEVEL = 2;
const MAX_HEADING_LEVEL = 4;

export default {
  name: 'LwPanel',
  props: {
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      default: null,
    },
    headingLevel: {
      type: Number,
      default: 3,
      validator: value => value >= MIN_HEADING_LEVEL && value <= MAX_HEADING_LEVEL,
    },
    surface: {
      type: String,
      default: 'app',
      validator: value => ['app', 'document'].includes(value),
    },
  },
  computed: {
    headingTag() {
      return `h${this.headingLevel}`;
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../sass/mixins.generated';

.lw-c-panel {
  background: var(--lw-elev-1-surface);
  box-shadow: var(--lw-elev-1-shadow);
  border: 1px solid var(--lw-elev-1-line);
  border-radius: var(--lw-radius-md);
}

// A document is still a card resting on the app canvas (the elev-1 shadow
// above is untouched), but its ground and edges come from the document
// token set rather than the elev scale: refresh_plan.md section 8 treats
// documents as "a different surface class from a live board", not a colour
// variant of one, which is why this is its own modifier rather than a prop
// that swaps a couple of CSS variables on the base class.
.lw-c-panel--document {
  background: var(--lw-document-paper);
  border-color: var(--lw-document-rule);
}

.lw-c-panel__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--lw-space-4);
  padding: var(--lw-space-4) var(--lw-space-5);
  border-bottom: 1px solid var(--lw-elev-1-line);
}

.lw-c-panel__heading-group {
  min-width: 0;
}

.lw-c-panel__title {
  margin: 0;
  // Subhead weight, per the contract: panel titles stop being the weakest
  // text on the page they title.
  font: var(--lw-type-subhead);
  color: var(--lw-fg-primary);
}

.lw-c-panel--document .lw-c-panel__head {
  // MANIFEST draws a heavier rule under a document's own head than a live
  // panel gets (direction-2.html .block__head sits inside a 2px .block
  // border, not a 1px hairline). The width is geometry with no token in the
  // scale, the same exception class as a chip's hairline border; only the
  // colour has to come from a token, and here it is the document ink itself
  // rather than a line token, because a document's rule is drawn in the same
  // ink as its text.
  border-bottom: 2px solid var(--lw-document-ink);
}

.lw-c-panel--document .lw-c-panel__title {
  color: var(--lw-document-ink);
}

.lw-c-panel__subtitle {
  margin: var(--lw-space-1) 0 0;
  font: var(--lw-type-body-sm);
  color: var(--lw-fg-secondary);
}

.lw-c-panel--document .lw-c-panel__subtitle {
  color: var(--lw-document-ink-muted);
}

.lw-c-panel__actions {
  flex: none;
  display: flex;
  align-items: center;
  gap: var(--lw-space-2);
}

.lw-c-panel__body {
  padding: var(--lw-space-5);
}
</style>
